import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CanonicalAssetImage } from "../../shared/CanonicalAsset";
import type { CanonicalAssetId } from "../../shared/canonical-assets";
import { Icon } from "../../shared/Icon";
import { voucherCodeHandoff } from "../next-ui/voucher/voucher-code-entry";
import { startBookHref } from "../next-ui/voucher/voucher-session";
import "./all-books-home.css";
import "./lc93-public-home.css";
import { lc93PublicHomeAssets } from "./lc93-public-home-assets";

type StartingPoint = {
  title: string;
  description: string;
  detail: string;
  note: string;
  route: string;
  action: string;
  asset: CanonicalAssetId;
  fallbackTitle: string;
  icon: keyof typeof lc93PublicHomeAssets.startingIcons;
};

const startingPoints: StartingPoint[] = [
  {
    title: "Preserve my story",
    description: "Shape memories, photographs, and your voice into a life story.",
    detail: "Hardcover example · matte",
    note: "A whole life, told your way.",
    route: "/books/story",
    action: "Explore My Story",
    asset: "LC-ASSET-BOOKS-FIXTURES-NARRATIVE-MY-STORY-CONTEXT",
    fallbackTitle: "My Story",
    icon: "botanical",
  },
  {
    title: "Tell our story",
    description: "How you met, the turning points and the life you’ve shared, told by one of you.",
    detail: "Hardcover example · matte",
    note: "The life you’ve shared.",
    route: "/books/love",
    action: "Explore Our Story",
    asset: "LC-ASSET-BOOKS-FIXTURES-NARRATIVE-OUR-STORY-CONTEXT",
    fallbackTitle: "Our Story",
    icon: "family",
  },
  {
    title: "Transform a memory",
    description: "Turn one photograph or moment into a picture-led story.",
    detail: "Softcover example · matte",
    note: "One moment can open a story.",
    route: "/books/children",
    action: "Explore the bedtime book",
    asset: "LC-ASSET-BOOKS-FIXTURES-PICTURE-LITTLE-MOMENTS-CONTEXT",
    fallbackTitle: "Little Moments",
    icon: "photograph",
  },
  {
    title: "Give a personal story",
    description: "Preview a gift that lets someone tell the story their way.",
    detail: "Gift preview · no charge",
    note: "They own the story they create.",
    route: "/gift",
    action: "Preview the gift experience",
    asset: "LC-ASSET-BOOKS-FIXTURES-RECIPE-FAMILY-RECIPES-CONTEXT",
    fallbackTitle: "Family Recipes",
    icon: "gift",
  },
];

const memoryStarters = [
  {
    title: "Start with a photo",
    description: "Build a picture-led story around one moment.",
    route: "/books/children",
    icon: "image",
  },
  {
    title: "Start with a voice note",
    description: "Explore a life-story book led by your own voice.",
    route: "/books/story",
    icon: "mic",
  },
  {
    title: "Start with a recipe",
    description: "Create a cookbook around recipes and the people behind them.",
    route: "/books/recipes",
    icon: "book",
  },
] as const;

function useHomepageMotion() {
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }

    let scrollFrame = 0;
    const updateHeaderConnection = () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        root.dataset.headerDetached = window.scrollY > 16 ? "true" : "false";
      });
    };
    window.addEventListener("scroll", updateHeaderConnection, { passive: true });
    updateHeaderConnection();

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return () => {
        window.cancelAnimationFrame(scrollFrame);
        window.removeEventListener("scroll", updateHeaderConnection);
        delete root.dataset.headerDetached;
      };
    }

    root.dataset.motionReady = "true";
    // Every section is drawn visible. Only a section still below the fold at load gets an
    // entrance, started just before it scrolls into view; one already being read is left alone,
    // and without an observer nothing animates. No section ever waits hidden for a scroll.
    let observer: IntersectionObserver | undefined;
    if ("IntersectionObserver" in window) {
      const fold = window.innerHeight;
      const waiting = Array.from(
        root.querySelectorAll<HTMLElement>("[data-motion='reveal']"),
      ).filter((target) => target.getBoundingClientRect().top >= fold);
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer?.unobserve(entry.target);
            // Only while it's still out of sight: one jumped to is already on screen, so it stays.
            if (entry.boundingClientRect.top >= window.innerHeight)
              entry.target.classList.add("is-revealing");
          }
        },
        { rootMargin: "0px 0px 15% 0px", threshold: 0 },
      );
      waiting.forEach((target) => observer?.observe(target));
    }

    const hero = root.querySelector<HTMLElement>(".lc93-home__hero");
    const precisePointer = window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;
    let pointerFrame = 0;

    const resetHeroDepth = () => {
      root.style.setProperty("--lc93-hero-depth-x", "0px");
      root.style.setProperty("--lc93-hero-depth-y", "0px");
    };
    const updateHeroDepth = (event: PointerEvent) => {
      if (!hero) return;
      window.cancelAnimationFrame(pointerFrame);
      pointerFrame = window.requestAnimationFrame(() => {
        const bounds = hero.getBoundingClientRect();
        const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * -10;
        const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * -6;
        root.style.setProperty("--lc93-hero-depth-x", `${x.toFixed(2)}px`);
        root.style.setProperty("--lc93-hero-depth-y", `${y.toFixed(2)}px`);
      });
    };

    if (hero && precisePointer) {
      hero.addEventListener("pointermove", updateHeroDepth);
      hero.addEventListener("pointerleave", resetHeroDepth);
    }

    return () => {
      observer?.disconnect();
      window.cancelAnimationFrame(scrollFrame);
      window.cancelAnimationFrame(pointerFrame);
      window.removeEventListener("scroll", updateHeaderConnection);
      hero?.removeEventListener("pointermove", updateHeroDepth);
      hero?.removeEventListener("pointerleave", resetHeroDepth);
      delete root.dataset.headerDetached;
      delete root.dataset.motionReady;
      resetHeroDepth();
    };
  }, []);

  return rootRef;
}

function Asset({
  id,
  alt,
  className,
  priority = false,
  decorative = false,
}: {
  id: CanonicalAssetId;
  alt?: string;
  className: string;
  priority?: boolean;
  decorative?: boolean;
}) {
  return (
    <CanonicalAssetImage
      id={id}
      alt={alt}
      decorative={decorative}
      className={className}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      fallback={
        decorative ? null : (
          <span className={`${className} lc93-home__asset-fallback`}>Fictional example</span>
        )
      }
    />
  );
}

/**
 * Most people arrive holding a voucher: its code goes straight to the voucher page, carried in the
 * address fragment (or this tab's history state), never in a query string. Nothing is checked or
 * used here; the voucher page checks it on arrival, without a second press (X1).
 */
function HeroVoucherCode() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [problem, setProblem] = useState("");
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const handoff = voucherCodeHandoff(code);
    if (!handoff) {
      setProblem(
        code.trim()
          ? "That’s longer than a voucher code. Check the code on your card, receipt or email."
          : "Enter the code from your voucher card, receipt or email.",
      );
      document.getElementById("lc93-voucher-code")?.focus();
      return;
    }
    navigate(handoff.to, { state: handoff.state });
  };
  return (
    <div className="lc93-home__voucher">
      <form
        className="lc93-home__frame lc93-home__voucher-form"
        aria-labelledby="lc93-voucher-label"
        noValidate
        onSubmit={submit}
      >
        <label
          id="lc93-voucher-label"
          className="lc93-home__voucher-label"
          htmlFor="lc93-voucher-code"
        >
          Have a voucher? <span>Enter your code</span>
        </label>
        <div className="lc93-home__voucher-row">
          <input
            id="lc93-voucher-code"
            name="voucher-code"
            className="lc93-home__voucher-input"
            value={code}
            maxLength={96}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            aria-describedby={
              problem ? "lc93-voucher-problem lc93-voucher-hint" : "lc93-voucher-hint"
            }
            aria-invalid={problem ? true : undefined}
            onChange={(event) => {
              setCode(event.target.value);
              if (problem) setProblem("");
            }}
          />
          <button className="lc93-home__button lc93-home__voucher-button" type="submit">
            Use your voucher <Icon name="arrow" size={18} />
          </button>
        </div>
        {problem && (
          <p id="lc93-voucher-problem" className="lc93-home__voucher-problem" role="alert">
            {problem}
          </p>
        )}
        <p id="lc93-voucher-hint" className="lc93-home__voucher-hint">
          It’s on your voucher card, receipt or email. Next you’ll see what it includes; nothing is
          used until you confirm your book.
        </p>
      </form>
    </div>
  );
}

export default function HomePage() {
  const rootRef = useHomepageMotion();

  return (
    <main
      ref={rootRef}
      id="main-content"
      className="customer-design-system public-story-home lc93-home"
    >
      <section className="lc93-home__hero" aria-labelledby="lc93-home-title">
        <div className="lc93-home__frame lc93-home__hero-frame">
          <div className="lc93-home__hero-copy">
            <h1 id="lc93-home-title">
              <span>Turn memories</span> <span>into a book</span> <span>together.</span>
            </h1>
            <p className="lc93-home__hero-lead">
              Start with one memory. Shape your book and review every page.
            </p>
            <div className="lc93-home__actions lc93-home__actions--hero">
              <Link className="lc93-home__button lc93-home__button--primary" to={startBookHref()}>
                Start a book <Icon name="arrow" size={18} />
              </Link>
              <Link className="lc93-home__text-link" to="/demo">
                See examples <Icon name="arrow" size={17} />
              </Link>
            </div>
          </div>

          <figure className="lc93-home__hero-art lc93-home__hero-art--premium">
            <img
              src={lc93PublicHomeAssets.heroCleanPlate.url}
              alt="Fictional open family book about Saturday morning pancakes with family photographs and keepsakes"
              className="lc93-home__hero-premium-stage"
              width={lc93PublicHomeAssets.heroCleanPlate.width}
              height={lc93PublicHomeAssets.heroCleanPlate.height}
              loading="eager"
              fetchPriority="high"
            />
            <figcaption>Fictional sample book</figcaption>
          </figure>

          <figure className="lc93-home__hero-art lc93-home__hero-art--mobile">
            <div className="lc93-home__hero-scene-window">
              <CanonicalAssetImage
                id="LC-ASSET-SCENES-EXACT-HERO-OPEN-BOOK"
                alt="Fictional open family book about Saturday morning pancakes with a family photograph"
                className="lc93-home__hero-scene"
                loading="eager"
                fetchPriority="high"
                fallback={
                  <Asset
                    id="LC-ASSET-BOOKS-FIXTURES-SATURDAY-MORNING-PANCAKES-HERO-SPREAD"
                    alt="Fictional open family book showing a Saturday morning pancakes story"
                    className="lc93-home__hero-scene lc93-home__hero-scene--fallback"
                    priority
                  />
                }
              />
            </div>
            <figcaption>Fictional sample book</figcaption>
          </figure>
        </div>
        <HeroVoucherCode />
      </section>

      <section className="lc93-home__starting" aria-labelledby="lc93-starting-title">
        <div className="lc93-home__frame lc93-home__starting-frame">
          <header
            className="lc93-home__starting-heading"
            data-motion="reveal"
            data-motion-kind="rule"
          >
            <span aria-hidden="true" />
            <h2 id="lc93-starting-title">Choose how you want to begin</h2>
            <p>Your story, your family, one memory, or a gift.</p>
          </header>
          <p className="lc93-home__starting-cue">
            Swipe to explore all four <Icon name="arrow" size={16} />
          </p>
          <ul className="lc93-home__starting-grid">
            {startingPoints.map((point) => (
              <li key={point.title} data-motion="reveal" data-motion-kind="keepsake">
                <Link
                  to={point.route}
                  aria-label={`${point.action}: ${point.title}`}
                  onFocus={(event) => {
                    const item = event.currentTarget.parentElement;
                    item?.scrollIntoView({
                      block: "nearest",
                      inline: "center",
                    });
                  }}
                >
                  <span className="lc93-home__starting-intro">
                    <span
                      className={`lc93-home__starting-icon lc93-home__starting-icon--${point.icon}`}
                    >
                      <img
                        src={lc93PublicHomeAssets.startingIcons[point.icon].url}
                        alt=""
                        aria-hidden="true"
                        width={lc93PublicHomeAssets.startingIcons[point.icon].width}
                        height={lc93PublicHomeAssets.startingIcons[point.icon].height}
                      />
                    </span>
                    <h3>{point.title}</h3>
                    <span>{point.description}</span>
                  </span>
                  <span className="lc93-home__cover-stage">
                    {point.icon === "family" ? (
                      <img
                        src={lc93PublicHomeAssets.ourStoryPair.url}
                        alt="Fictional Our Story and Our Family Story keepsake books"
                        className="lc93-home__cover lc93-home__cover--our-story-pair"
                        width={lc93PublicHomeAssets.ourStoryPair.width}
                        height={lc93PublicHomeAssets.ourStoryPair.height}
                      />
                    ) : (
                      <Asset
                        id={point.asset}
                        alt={`Fictional ${point.fallbackTitle} book example in a keepsake setting`}
                        className={`lc93-home__cover lc93-home__cover--${point.icon}`}
                      />
                    )}
                  </span>
                  <span className="lc93-home__starting-detail">
                    <small>{point.detail}</small>
                    <em>{point.note}</em>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="lc93-home__fixture-note">
            Fictional book examples · sign in to work with your own private materials
          </p>
        </div>
      </section>

      <section className="lc93-home__memory" aria-labelledby="lc93-memory-title">
        <div className="lc93-home__frame lc93-home__memory-grid">
          <div className="lc93-home__memory-copy" data-motion="reveal" data-motion-kind="copy">
            <h2 id="lc93-memory-title">
              <span>You already have</span> <span>a place to start.</span>
            </h2>
            <p>A photograph, voice note, recipe card, or unfinished story is enough to begin.</p>
          </div>
          <figure
            className="lc93-home__memory-sources"
            data-motion="reveal"
            data-motion-kind="memory-sources"
          >
            <img
              src={lc93PublicHomeAssets.memoryArrowCallout.url}
              alt=""
              aria-hidden="true"
              className="lc93-home__memory-callout"
              width={lc93PublicHomeAssets.memoryArrowCallout.width}
              height={lc93PublicHomeAssets.memoryArrowCallout.height}
            />
            <Asset
              id="LC-ASSET-MEMORY-ARTIFACTS-VOICE-MEMO-STRIP"
              alt="Fictional voice-note playback control"
              className="lc93-home__memory-voice"
            />
            <Asset
              id="LC-ASSET-MEMORY-ARTIFACTS-HANDWRITTEN-RECIPE"
              alt="Fictional handwritten family recipe"
              className="lc93-home__memory-recipe"
            />
            <Asset
              id="LC-ASSET-MEMORY-ARTIFACTS-ARCHIVAL-PHOTO"
              alt="Fictional archival family photograph"
              className="lc93-home__memory-photo"
            />
          </figure>
          <figure
            className="lc93-home__memory-result"
            data-motion="reveal"
            data-motion-kind="finished-book"
          >
            <Asset
              id="LC-ASSET-BOOKS-FIXTURES-MORE-THAN-PANCAKES-SPREAD"
              alt="Fictional finished spread titled More Than Pancakes"
              className="lc93-home__memory-spread"
            />
            <figcaption className="sr-only">A fictional family-reviewed finished page</figcaption>
          </figure>
        </div>
      </section>

      <section className="lc93-home__process" aria-labelledby="lc93-process-title">
        <div className="lc93-home__frame">
          <h2 id="lc93-process-title" className="sr-only">
            From one memory to a book your family can review
          </h2>
          <ol data-motion="reveal" data-motion-kind="process">
            <li>
              <span>1</span>
              <div>
                <strong>Add what you remember</strong>
                <p>Record a story or add photographs, voice notes, and recipes.</p>
              </div>
            </li>
            <li>
              <span>2</span>
              <div>
                <strong>Shape the book</strong>
                <p>Organize memories into pages you can read and edit.</p>
              </div>
            </li>
            <li>
              <span>3</span>
              <div>
                <strong>Review every page</strong>
                <p>Check the words and pictures, then approve what belongs.</p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <section className="lc93-home__family" aria-labelledby="lc93-family-title">
        <div className="lc93-home__frame lc93-home__family-grid">
          <div className="lc93-home__family-copy" data-motion="reveal" data-motion-kind="copy">
            <h2 id="lc93-family-title">
              <span>Bring in the people</span> <span>who remember it too.</span>
            </h2>
            <p>
              Gather the memories your family wants to preserve. This fictional example shows a
              shared book; inviting family to help isn’t available yet.
            </p>
            <Link className="lc93-home__button lc93-home__button--primary" to="/demo/family">
              See the fictional family example <Icon name="arrow" size={18} />
            </Link>
            <Link className="lc93-home__family-review" to="/demo/family">
              Explore the family story
            </Link>
          </div>
          <div
            className="lc93-home__family-art"
            data-motion="reveal"
            data-motion-kind="family-book"
          >
            <img
              src={lc93PublicHomeAssets.familyEucalyptusNote.url}
              alt=""
              aria-hidden="true"
              className="lc93-home__family-decoration"
              width={lc93PublicHomeAssets.familyEucalyptusNote.width}
              height={lc93PublicHomeAssets.familyEucalyptusNote.height}
            />
            <Asset
              id="LC-ASSET-BOOKS-FIXTURES-GRANDMAS-GARDEN-SPREAD"
              alt="Fictional family-reviewed book spread titled Grandma's Garden"
              className="lc93-home__family-book"
            />
            <div
              className="lc93-home__activity"
              role="group"
              aria-label="Fictional collaboration activity"
            >
              <p data-position="maya">
                <img src={lc93PublicHomeAssets.familyAvatars.maya.url} alt="" aria-hidden="true" />
                <strong>Maya added a photo</strong>
                <small>2 days ago</small>
              </p>
              <p data-position="aunt-elena">
                <img
                  src={lc93PublicHomeAssets.familyAvatars.auntElena.url}
                  alt=""
                  aria-hidden="true"
                />
                <strong>Aunt Elena left a comment</strong>
                <small>Yesterday</small>
              </p>
              <p data-position="sam">
                <img src={lc93PublicHomeAssets.familyAvatars.sam.url} alt="" aria-hidden="true" />
                <strong>Sam approved the opening</strong>
                <small>3 hours ago</small>
              </p>
              <p data-position="mom">
                <img src={lc93PublicHomeAssets.familyAvatars.mom.url} alt="" aria-hidden="true" />
                <strong>Mom suggested an edit</strong>
                <small>1 day ago</small>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="lc93-home__closing" aria-labelledby="lc93-closing-title">
        <div className="lc93-home__frame lc93-home__closing-grid">
          <div
            className="lc93-home__closing-action"
            data-motion="reveal"
            data-motion-kind="closing-copy"
          >
            <h2 id="lc93-closing-title">What would you like to preserve?</h2>
            <p>
              Choose a book by its purpose, or begin with the photograph, voice note, or recipe
              already in front of you.
            </p>
            <div className="lc93-home__closing-actions">
              <Link className="lc93-home__button lc93-home__button--primary" to="/books">
                Find the right book <Icon name="arrow" size={18} />
              </Link>
              <Link className="lc93-home__closing-link" to="/how-it-works">
                See how it works <Icon name="arrow" size={17} />
              </Link>
            </div>
          </div>
          <div className="lc93-home__starter" data-motion="reveal" data-motion-kind="starter">
            <h3>Start with what is easiest to reach.</h3>
            <p>Choose one piece of family material. You can add the rest as the book grows.</p>
            <ul className="lc93-home__starter-list">
              {memoryStarters.map((starter) => (
                <li key={starter.title}>
                  <Link to={starter.route}>
                    <Icon name={starter.icon} size={21} />
                    <span>
                      <strong>{starter.title}</strong>
                      <small>{starter.description}</small>
                    </span>
                    <Icon name="arrow" size={17} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </main>
  );
}
